import { create } from "zustand";
import { Message } from "../types/conversation/message";
import { Conversation } from "../types/conversation/conversation";

interface ConversationData {
  conversation?: Conversation;
  messages: Message[];
  hasMore: boolean;
  loading: boolean;
  nextId?: string;
  error: string | null;
  hasUnreadMessages?: boolean;
}

interface ConversationState {
  conversations: { [conversationId: string]: ConversationData };
  currentConversationId: string | null;
}

interface ConversationActions {
  setCurrentConversationId: (id: string | null) => void;
  addConversation: (conversation: Conversation) => void;
  addConversationId: (conversationId: string) => void;
  updateUnreadConversation: (payload: {
    conversationId: string;
    hasUnreadMessages: boolean;
  }) => void;
  getMessagesStart: (conversationId: string) => void;
  getMessagesSuccess: (payload: {
    conversationId: string;
    messages: Message[];
    hasMore: boolean;
    nextId?: string;
  }) => void;
  getMessagesFailure: (payload: {
    conversationId: string;
    error: string;
  }) => void;
  addMessage: (payload: { conversationId: string; message: Message }) => void;
  clearMessages: (conversationId: string) => void;
  updateMessage: (payload: { conversationId: string; message: Message }) => void;
  removeMessage: (payload: {
    conversationId: string;
    messageId: string;
  }) => void;
  setLoading: (payload: { conversationId: string; loading: boolean }) => void;
  reset: () => void;
}

export type ConversationStore = ConversationState & ConversationActions;

const initialConversationData: ConversationData = {
  messages: [],
  hasMore: true,
  loading: false,
  error: null,
};

const initialState: ConversationState = {
  conversations: {},
  currentConversationId: null,
};

export const useConversationStore = create<ConversationStore>((set) => ({
  ...initialState,

  setCurrentConversationId: (currentConversationId) =>
    set({ currentConversationId }),

  addConversation: (conversation) =>
    set((state) => {
      if (state.conversations[conversation.id]) return state;
      return {
        conversations: {
          ...state.conversations,
          [conversation.id]: {
            ...initialConversationData,
            conversation,
          },
        },
      };
    }),

  addConversationId: (conversationId) =>
    set((state) => {
      if (state.conversations[conversationId]) return state;
      return {
        conversations: {
          ...state.conversations,
          [conversationId]: { ...initialConversationData },
        },
      };
    }),

  updateUnreadConversation: ({ conversationId, hasUnreadMessages }) =>
    set((state) => {
      if (!state.conversations[conversationId]) return state;
      return {
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...state.conversations[conversationId],
            hasUnreadMessages,
          },
        },
      };
    }),

  getMessagesStart: (conversationId) =>
    set((state) => {
      if (!state.conversations[conversationId]) return state;
      return {
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...state.conversations[conversationId],
            loading: true,
            error: null,
          },
        },
      };
    }),

  getMessagesSuccess: ({ conversationId, messages, hasMore, nextId }) =>
    set((state) => {
      if (!state.conversations[conversationId]) return state;
      return {
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...state.conversations[conversationId],
            messages: [
              ...state.conversations[conversationId].messages,
              ...messages,
            ],
            hasMore,
            loading: false,
            nextId,
          },
        },
      };
    }),

  getMessagesFailure: ({ conversationId, error }) =>
    set((state) => {
      if (!state.conversations[conversationId]) return state;
      return {
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...state.conversations[conversationId],
            loading: false,
            error,
          },
        },
      };
    }),

  addMessage: ({ conversationId, message }) =>
    set((state) => {
      if (!state.conversations[conversationId]) return state;
      return {
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...state.conversations[conversationId],
            messages: [message, ...state.conversations[conversationId].messages],
          },
        },
      };
    }),

  clearMessages: (conversationId) =>
    set((state) => {
      if (!state.conversations[conversationId]) return state;
      return {
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...state.conversations[conversationId],
            messages: [],
            hasMore: true,
          },
        },
      };
    }),

  updateMessage: ({ conversationId, message }) =>
    set((state) => {
      const conversationData = state.conversations[conversationId];
      if (!conversationData) return state;

      const messageIndex = conversationData.messages.findIndex(
        (msg) => msg.id === message.id
      );
      if (messageIndex === -1) return state;

      const updatedMessages = [...conversationData.messages];
      updatedMessages[messageIndex] = message;

      return {
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...conversationData,
            messages: updatedMessages,
          },
        },
      };
    }),

  removeMessage: ({ conversationId, messageId }) =>
    set((state) => {
      if (!state.conversations[conversationId]) return state;
      return {
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...state.conversations[conversationId],
            messages: state.conversations[conversationId].messages.filter(
              (msg) => msg.id !== messageId
            ),
          },
        },
      };
    }),

  setLoading: ({ conversationId, loading }) =>
    set((state) => {
      if (!state.conversations[conversationId]) return state;
      return {
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...state.conversations[conversationId],
            loading,
          },
        },
      };
    }),
  
  reset: () => set(initialState),
}));
